<template>
    <AppLayout>
        <div class="bg-white">
            <div>
                <!-- Mobile filter dialog -->
                <TransitionRoot as="template" :show="mobileFiltersOpen">
                    <Dialog class="relative z-40 lg:hidden" @close="mobileFiltersOpen = false">
                        <TransitionChild as="template" enter="transition-opacity ease-linear duration-300"
                            enter-from="opacity-0" enter-to="opacity-100"
                            leave="transition-opacity ease-linear duration-300" leave-from="opacity-100"
                            leave-to="opacity-0">
                            <div class="fixed inset-0 bg-black/25" />
                        </TransitionChild>

                        <div class="fixed inset-0 z-40 flex">
                            <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                                enter-from="translate-x-full" enter-to="translate-x-0"
                                leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                                leave-to="translate-x-full">
                                <DialogPanel
                                    class="relative ml-auto flex size-full max-w-xs flex-col overflow-y-auto bg-white pt-4 pb-6 shadow-xl">
                                    <div class="flex items-center justify-between px-4">
                                        <h2 class="text-lg font-medium text-ink-900">Filters</h2>
                                        <button type="button"
                                            class="relative -mr-2 flex size-10 items-center justify-center rounded-xl bg-white p-2 text-ink-400 hover:bg-ink-50 focus:ring-2 focus:ring-primary-500 focus:outline-hidden"
                                            @click="mobileFiltersOpen = false">
                                            <span class="absolute -inset-0.5" />
                                            <span class="sr-only">Close menu</span>
                                            <XMarkIcon class="size-6" aria-hidden="true" />
                                        </button>
                                    </div>

                                    <!-- Filters -->
                                    <form class="mt-4 border-t border-ink-200">
                                        <h3 class="sr-only">Categories</h3>
                                        <ul role="list" class="px-2 py-3 font-medium text-ink-900">
                                            <li v-for="section in sections" :key="section.name">
                                                <a :href="section.href" class="block px-2 py-3"
                                                    @click="activeSection = section.name">{{ section.name }}</a>
                                            </li>
                                        </ul>

                                        <Disclosure as="div" v-for="section in filters" :key="section.id"
                                            class="border-t border-ink-200 px-4 py-6" v-slot="{ open }">
                                            <h3 class="-mx-2 -my-3 flow-root">
                                                <DisclosureButton
                                                    class="flex w-full items-center justify-between bg-white px-2 py-3 text-ink-400 hover:text-ink-500">
                                                    <span class="font-medium text-ink-900">{{ section.name }}</span>
                                                    <span class="ml-6 flex items-center">
                                                        <PlusIcon v-if="!open" class="size-5" aria-hidden="true" />
                                                        <MinusIcon v-else class="size-5" aria-hidden="true" />
                                                    </span>
                                                </DisclosureButton>
                                            </h3>
                                            <DisclosurePanel class="pt-6">
                                                <div class="space-y-6">
                                                    <div v-for="(option, optionIdx) in section.options"
                                                        :key="option.value" class="flex gap-3">
                                                        <div class="flex h-5 shrink-0 items-center">
                                                            <div class="group grid size-4 grid-cols-1">
                                                                <input :id="`filter-mobile-${section.id}-${optionIdx}`"
                                                                    :name="`${section.id}[]`" :value="option.value"
                                                                    type="checkbox"
                                                                    class="col-start-1 row-start-1 appearance-none rounded-sm border border-ink-300 bg-white checked:border-primary-600 checked:bg-primary-600 indeterminate:border-primary-600 indeterminate:bg-primary-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:border-ink-300 disabled:bg-ink-100 disabled:checked:bg-ink-100 forced-colors:appearance-auto" />
                                                                <svg class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-ink-950/25"
                                                                    viewBox="0 0 14 14" fill="none">
                                                                    <path
                                                                        class="opacity-0 group-has-checked:opacity-100"
                                                                        d="M3 8L6 11L11 3.5" stroke-width="2"
                                                                        stroke-linecap="round"
                                                                        stroke-linejoin="round" />
                                                                    <path
                                                                        class="opacity-0 group-has-indeterminate:opacity-100"
                                                                        d="M3 7H11" stroke-width="2"
                                                                        stroke-linecap="round"
                                                                        stroke-linejoin="round" />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                        <label :for="`filter-mobile-${section.id}-${optionIdx}`"
                                                            class="min-w-0 flex-1 text-ink-500">{{ option.label
                                                            }}</label>
                                                    </div>
                                                </div>
                                            </DisclosurePanel>
                                        </Disclosure>
                                    </form>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </Dialog>
                </TransitionRoot>

                <main class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div class="flex items-baseline justify-between border-b border-ink-200 pt-24 pb-6">
                        <h1 class="font-display text-4xl font-bold tracking-tight text-ink-900">Learning</h1>

                        <div class="flex items-center">
                            <Menu as="div" class="relative inline-block text-left">
                                <div>
                                    <MenuButton
                                        class="group inline-flex justify-center text-sm font-medium text-ink-700 hover:text-ink-900">
                                        Sort
                                        <ChevronDownIcon
                                            class="-mr-1 ml-1 size-5 shrink-0 text-ink-400 group-hover:text-ink-500"
                                            aria-hidden="true" />
                                    </MenuButton>
                                </div>

                                <transition enter-active-class="transition ease-out duration-100"
                                    enter-from-class="transform opacity-0 scale-95"
                                    enter-to-class="transform opacity-100 scale-100"
                                    leave-active-class="transition ease-in duration-75"
                                    leave-from-class="transform opacity-100 scale-100"
                                    leave-to-class="transform opacity-0 scale-95">
                                    <MenuItems
                                        class="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-xl bg-white shadow-2xl ring-1 ring-black/5 focus:outline-hidden">
                                        <div class="py-1">
                                            <MenuItem v-for="option in sortOptions" :key="option.name"
                                                v-slot="{ active }">
                                            <a :href="option.href"
                                                :class="[option.current ? 'font-medium text-ink-900' : 'text-ink-500', active ? 'bg-ink-100 outline-hidden' : '', 'block px-4 py-2 text-sm']">{{
                                                option.name }}</a>
                                            </MenuItem>
                                        </div>
                                    </MenuItems>
                                </transition>
                            </Menu>

                            <button type="button" class="-m-2 ml-5 p-2 text-ink-400 hover:text-ink-500 sm:ml-7">
                                <span class="sr-only">View grid</span>
                                <Squares2X2Icon class="size-5" aria-hidden="true" />
                            </button>
                            <button type="button"
                                class="-m-2 ml-4 p-2 text-ink-400 hover:text-ink-500 sm:ml-6 lg:hidden"
                                @click="mobileFiltersOpen = true">
                                <span class="sr-only">Filters</span>
                                <FunnelIcon class="size-5" aria-hidden="true" />
                            </button>
                        </div>
                    </div>

                    <section aria-labelledby="products-heading" class="pt-6 pb-24">
                        <h2 id="products-heading" class="sr-only">Products</h2>

                        <div class="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-4">
                            <!-- Filters -->
                            <div class="bg-white max-w-2xl">
                                <ul class="bg-ink-100 min-w-[150px] max-sm:min-w-[110px] w-full">
                                    <li id="coursesTab" @click="activeSection = 'courses'"
                                        class="tab text-ink-600 hover:text-white hover:bg-primary-500 py-4 flex flex-row items-center justify-start font-semibold w-full text-[15px] px-4 border-r-4 cursor-pointer"
                                        :class="activeSection === 'courses' ? 'text-white border-primary-600 bg-primary-500' : 'text-ink-600 border-ink-100'">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                                            class="w-5 h-5 mb-2 me-2" viewBox="0 0 511 511.999">
                                            <path
                                                d="M498.7 222.695c-.016-.011-.028-.027-.04-.039L289.805 13.81C280.902 4.902 269.066 0 256.477 0c-12.59 0-24.426 4.902-33.332 13.809L14.398 222.55c-.07.07-.144.144-.21.215-18.282 18.386-18.25 48.218.09 66.558 8.378 8.383 19.44 13.235 31.273 13.746.484.047.969.07 1.457.07h8.32v153.696c0 30.418 24.75 55.164 55.168 55.164h81.711c8.285 0 15-6.719 15-15V376.5c0-13.879 11.293-25.168 25.172-25.168h48.195c13.88 0 25.168 11.29 25.168 25.168V497c0 8.281 6.715 15 15 15h81.711c30.422 0 55.168-24.746 55.168-55.164V303.14h7.719c12.586 0 24.422-4.903 33.332-13.813 18.36-18.367 18.367-48.254.027-66.633zm-21.243 45.422a17.03 17.03 0 0 1-12.117 5.024h-22.72c-8.285 0-15 6.714-15 15v168.695c0 13.875-11.289 25.164-25.168 25.164h-66.71V376.5c0-30.418-24.747-55.168-55.169-55.168H232.38c-30.422 0-55.172 24.75-55.172 55.168V482h-66.71c-13.876 0-25.169-11.29-25.169-25.164V288.14c0-8.286-6.715-15-15-15H48a13.9 13.9 0 0 0-.703-.032c-4.469-.078-8.66-1.851-11.8-4.996-6.68-6.68-6.68-17.55 0-24.234.003 0 .003-.004.007-.008l.012-.012L244.363 35.02A17.003 17.003 0 0 1 256.477 30c4.574 0 8.875 1.781 12.113 5.02l208.8 208.796.098.094c6.645 6.692 6.633 17.54-.031 24.207zm0 0"
                                                data-original="#000000" />
                                        </svg>
                                        Courses
                                    </li>
                                    <li id="booksTab" @click="activeSection = 'books'"
                                        class="tab hover:text-white hover:bg-primary-500 py-4 flex flex-row items-center justify-start font-semibold w-full text-[15px] px-4 border-r-4 cursor-pointer"
                                        :class="activeSection === 'books' ? 'text-white border-primary-600 bg-primary-500' : 'text-ink-600 border-ink-100'">

                                        <svg xmlns="http://www.w3.org/2000/svg" stroke="currentColor"
                                            class="w-5 h-5 mb-2 me-2" viewBox="0 0 682.667 682.667">
                                            <defs>
                                                <clipPath id="a" clipPathUnits="userSpaceOnUse">
                                                    <path d="M0 512h512V0H0Z" data-original="#000000" />
                                                </clipPath>
                                            </defs>
                                            <g clip-path="url(#a)" transform="matrix(1.33 0 0 -1.33 0 682.667)">
                                                <path fill="none" stroke-linecap="round" stroke-linejoin="round"
                                                    stroke-miterlimit="10" stroke-width="40"
                                                    d="M256 334.666c-43.446 0-78.667-35.22-78.667-78.667 0-43.446 35.221-78.666 78.667-78.666 43.446 0 78.667 35.22 78.667 78.666 0 43.447-35.221 78.667-78.667 78.667Zm220.802-22.53-21.299-17.534c-24.296-20.001-24.296-57.204 0-77.205l21.299-17.534c7.548-6.214 9.497-16.974 4.609-25.441l-42.057-72.845c-4.889-8.467-15.182-12.159-24.337-8.729l-25.835 9.678c-29.469 11.04-61.688-7.561-66.862-38.602l-4.535-27.213c-1.607-9.643-9.951-16.712-19.727-16.712h-84.116c-9.776 0-18.12 7.069-19.727 16.712l-4.536 27.213c-5.173 31.041-37.392 49.642-66.861 38.602l-25.834-9.678c-9.156-3.43-19.449.262-24.338 8.729l-42.057 72.845c-4.888 8.467-2.939 19.227 4.609 25.441l21.3 17.534c24.295 20.001 24.295 57.204 0 77.205l-21.3 17.534c-7.548 6.214-9.497 16.974-4.609 25.441l42.057 72.845c4.889 8.467 15.182 12.159 24.338 8.729l25.834-9.678c29.469-11.04 61.688 7.561 66.861 38.602l4.536 27.213c1.607 9.643 9.951 16.711 19.727 16.711h84.116c9.776 0 18.12-7.068 19.727-16.711l4.535-27.213c5.174-31.041 37.393-49.642 66.862-38.602l25.835 9.678c9.155 3.43 19.448-.262 24.337-8.729l42.057-72.845c4.888-8.467 2.939-19.227-4.609-25.441z"
                                                    data-original="#000000" />
                                            </g>
                                        </svg>
                                        Books
                                    </li>
                                    <li id="certificateTab" @click="activeSection = 'certificates'"
                                        class="tab text-ink-600 hover:text-white hover:bg-primary-500 py-4 flex flex-row items-center justify-start font-semibold w-full text-[15px] px-4 border-r-4 cursor-pointer"
                                        :class="activeSection === 'certificates' ? 'text-white border-primary-600 bg-primary-500' : 'text-ink-600 border-ink-100'">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                                            class="w-5 h-5 mb-2 me-2" viewBox="0 0 512 512">
                                            <path
                                                d="M437.02 74.98C388.668 26.63 324.379 0 256 0S123.332 26.629 74.98 74.98C26.63 123.332 0 187.621 0 256s26.629 132.668 74.98 181.02C123.332 485.37 187.621 512 256 512s132.668-26.629 181.02-74.98C485.37 388.668 512 324.379 512 256s-26.629-132.668-74.98-181.02zM111.105 429.297c8.454-72.735 70.989-128.89 144.895-128.89 38.96 0 75.598 15.179 103.156 42.734 23.281 23.285 37.965 53.687 41.742 86.152C361.641 462.172 311.094 482 256 482s-105.637-19.824-144.895-52.703zM256 269.507c-42.871 0-77.754-34.882-77.754-77.753C178.246 148.879 213.13 114 256 114s77.754 34.879 77.754 77.754c0 42.871-34.883 77.754-77.754 77.754zm170.719 134.427a175.9 175.9 0 0 0-46.352-82.004c-18.437-18.438-40.25-32.27-64.039-40.938 28.598-19.394 47.426-52.16 47.426-89.238C363.754 132.34 315.414 84 256 84s-107.754 48.34-107.754 107.754c0 37.098 18.844 69.875 47.465 89.266-21.887 7.976-42.14 20.308-59.566 36.542-25.235 23.5-42.758 53.465-50.883 86.348C50.852 364.242 30 312.512 30 256 30 131.383 131.383 30 256 30s226 101.383 226 226c0 56.523-20.86 108.266-55.281 147.934zm0 0"
                                                data-original="#000000" />
                                        </svg>
                                        Certificates
                                    </li>
                                </ul>
                            </div>

                            <!-- Product grid -->
                            <div class="lg:col-span-3">
                                <!-- Your content -->

                                <div v-if="activeSection === 'courses'">
                                    <h3 class="text-lg font-medium text-ink-900 mb-4">My Courses</h3>
                                    <div class="overflow-x-auto">
                                        <table class="min-w-full bg-white">
                                            <thead class="bg-ink-800 whitespace-nowrap">
                                                <tr>
                                                    <th class="p-4 text-left text-sm font-medium text-white">
                                                        Course
                                                    </th>
                                                    <th class="p-4 text-left text-sm font-medium text-white">
                                                        Tier
                                                    </th>
                                                    <th class="p-4 text-left text-sm font-medium text-white">
                                                        Start
                                                    </th>
                                                    <th class="p-4 text-left text-sm font-medium text-white">
                                                        End
                                                    </th>
                                                    <th class="p-4 text-left text-sm font-medium text-white">
                                                        Status
                                                    </th>
                                                    <th class="p-4 text-left text-sm font-medium text-white">
                                                        Progress
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody class="whitespace-nowrap">
                                                <tr v-for="subscription in mySubscriptions" class="even:bg-primary-50">
                                                    <td class="p-4 text-[15px] text-ink-900 font-medium">
                                                        {{ subscription.product?.name }}
                                                    </td>
                                                    <td class="p-4 text-[15px] text-ink-600 font-medium">
                                                        {{ subscription.tier?.name }}
                                                    </td>
                                                    <td class="p-4 text-[15px] text-ink-600 font-medium">
                                                        {{ subscription.started_at }}
                                                    </td>
                                                    <td class="p-4 text-[15px] text-ink-600 font-medium">
                                                        {{ subscription.ended_at }}
                                                    </td>
                                                    <td class="p-4 text-[15px] text-ink-600 font-medium">
                                                        {{ subscription.status }}
                                                    </td>
                                                    <td class="p-4 text-[15px] text-ink-600 font-medium">
                                                        <Button size="small"
                                                            @click="scheduleModal.show = true; scheduleModal.schedules = subscription.schedules">
                                                            View Schedule
                                                        </Button>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        <div id="modal" v-if="scheduleModal.show"
                                            class="fixed inset-0 z-50 flex items-center justify-center">
                                            <div
                                                class="fixed inset-0 p-4 flex flex-wrap justify-center items-center w-full h-full z-[1000] before:fixed before:inset-0 before:w-full before:h-full before:bg-[rgba(0,0,0,0.5)] overflow-auto">
                                                <div
                                                    class="w-full max-w-xlg bg-white shadow-lg rounded-xl p-6 relative">
                                                    <div class="flex items-center pb-3 border-b border-ink-300">
                                                        <h3 class="text-ink-900 text-xl font-semibold flex-1">
                                                            Schedule Details
                                                        </h3>
                                                        <svg id="closeIcon" @click="scheduleModal.show = false"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            class="w-3.5 h-3.5 ml-2 cursor-pointer shrink-0 fill-ink-400 hover:fill-red-500"
                                                            viewBox="0 0 320.591 320.591">
                                                            <path
                                                                d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z"
                                                                data-original="#000000"></path>
                                                            <path
                                                                d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z"
                                                                data-original="#000000"></path>
                                                        </svg>
                                                    </div>

                                                    <div class="my-6">
                                                        <p v-if="!scheduleModal.schedules?.length"
                                                            class="text-ink-800 text-sm">
                                                            No schedules available for this subscription.
                                                        </p>
                                                        <table v-else class="min-w-full">
                                                            <thead class="bg-ink-100">
                                                                <tr>
                                                                    <th
                                                                        class="p-4 text-left text-sm font-medium text-ink-700">
                                                                        Title</th>
                                                                    <th
                                                                        class="p-4 text-left text-sm font-medium text-ink-700">
                                                                        Link</th>
                                                                    <th
                                                                        class="p-4 text-left text-sm font-medium text-ink-700">
                                                                        Starts at</th>
                                                                    <th
                                                                        class="p-4 text-left text-sm font-medium text-ink-700">
                                                                        Ends at</th>
                                                                    <th
                                                                        class="p-4 text-left text-sm font-medium text-ink-700">
                                                                        Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr v-for="schedule in scheduleModal.schedules"
                                                                    :key="schedule.id">
                                                                    <td class="p-4">{{ schedule.title }}</td>
                                                                    <td class="p-4">
                                                                        <a :href="schedule.link" target="_blank"
                                                                            class="text-primary-700 hover:underline">
                                                                            {{ schedule.link?.substring(0, 30) }}{{
                                                                            schedule.link?.length > 30 ? '...' : '' }}
                                                                        </a>
                                                                    </td>
                                                                    <td class="p-4">{{ schedule.started_at }}</td>
                                                                    <td class="p-4">{{ schedule.ended_at }}</td>
                                                                    <td class="p-4">
                                                                        <span :class="{
                                                                            'text-green-600': schedule.status === 'active',
                                                                            'text-red-600': schedule.status === 'inactive'
                                                                        }">{{ schedule.status }}</span>
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    <div class="border-t border-ink-300 pt-6 flex justify-end gap-4">
                                                        <button id="closeButton" type="button"
                                                            class="px-4 py-2 rounded-xl text-ink-900 text-sm font-medium border-none outline-none tracking-wide bg-ink-200 hover:bg-ink-300 active:bg-ink-200"
                                                            @click="scheduleModal.show = false">Close</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-else-if="activeSection === 'books'">
                                    <h3 class="text-lg font-medium text-ink-900 mb-4">My Books</h3>
                                    <!-- Book content goes here -->
                                    <p v-if="myBooks.length === 0">No books available.</p>
                                    <div
                                        class="grid items-start grid-cols-1 lg:grid-cols-2 space-y-4 lg:space-y-0 lg:gap-4">
                                        <div v-for="book in myBooks" :key="book.id" @click="bookModal.show = true; bookModal.book = book"
                                            class="bg-white h-full p-4 rounded-xl shadow hover:shadow-lg transition-shadow">
                                            <img :src="book.thumbnail" alt="Book Image"
                                                class="w-full h-48 object-cover mb-2 rounded">
                                            <h4 class="text-md font-semibold text-ink-800">{{ book.title }}</h4>
                                        </div>
                                    </div>

                                    <Galleria v-model:visible="bookModal.show" :value="bookModal.book?.media || []"
                                        :responsiveOptions="responsiveOptions" :numVisible="9"
                                        containerStyle="max-width: 60vh" :circular="true" :fullScreen="true"
                                        :showItemNavigators="true">
                                        <template #item="slotProps">
                                            {{ console.log(slotProps.item) }}
                                            <img :src="slotProps.item.url" :alt="slotProps.item.name"
                                                style="width: 100%; display: block" />
                                        </template>
                                        <template #thumbnail="slotProps">
                                            <img :src="slotProps.item.url" :alt="slotProps.item.name" style="display: block" />
                                        </template>
                                    </Galleria>
                                </div>

                                <div v-else-if="activeSection === 'certificates'">
                                    <h3 class="text-lg font-medium text-ink-900 mb-4">My Certificates</h3>
                                    <!-- Certificate content goes here -->
                                    <p>No certificates available.</p>
                                </div>

                            </div>
                        </div>
                    </section>
                </main>
            </div>
        </div>
    </AppLayout>
</template>

<script setup>
import AppLayout from '@/components/layouts/AppLayout.vue';
import { ref, watch } from 'vue';
import {
    Dialog,
    DialogPanel,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue';
import Galleria from 'primevue/galleria';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { ChevronDownIcon, FunnelIcon, MinusIcon, PlusIcon, Squares2X2Icon } from '@heroicons/vue/20/solid';
import { getMySubscriptions } from '@/services/learningService.js';
import Button from 'primevue/button';
import { getUserBooks } from '@/services/userBookService';

const activeSection = ref('courses');
const mySubscriptions = ref([]);
const myBooks = ref([]);
const scheduleModal = ref({
    show: false,
    schedules: [],
});
const bookModal = ref({
    show: false,
    book: null,
});

watch(scheduleModal, (newValue) => {
    console.log('Schedule Modal Changed:', newValue);
});

const sortOptions = [
    { name: 'Most Popular', href: '#', current: true },
    { name: 'Best Rating', href: '#', current: false },
    { name: 'Newest', href: '#', current: false },
    { name: 'Price: Low to High', href: '#', current: false },
    { name: 'Price: High to Low', href: '#', current: false },
]
const sections = [
    { name: 'Courses', href: '#', current: true },
    { name: 'Books', href: '#', current: false },
    { name: 'Certificates', href: '#', current: false },
];

const mobileFiltersOpen = ref(false)

const filters = [
    {
        id: 'category',
        name: 'Category',
        options: [
            { value: 'all', label: 'All' },
            { value: 'language', label: 'Language' },
            { value: 'culture', label: 'Culture' },
            { value: 'history', label: 'History' },
        ],
    },
    {
        id: 'level',
        name: 'Level',
        options: [
            { value: 'beginner', label: 'Beginner' },
            { value: 'intermediate', label: 'Intermediate' },
            { value: 'advanced', label: 'Advanced' },
        ],
    },
];

// Fetch my subscriptions

const fetchMySubscriptions = async () => {
    try {
        const response = await getMySubscriptions();
        mySubscriptions.value = response.data;
        console.log('My Subscriptions:', mySubscriptions.value);
    } catch (error) {
        console.error('Error fetching my subscriptions:', error);
    }
};

const fetchMyBooks = async () => {
    try {
        const response = await getUserBooks();
        myBooks.value = response.data;
        console.log('My Books:', myBooks.value);
    } catch (error) {
        console.error('Error fetching my books:', error);
    }
};

fetchMySubscriptions();
fetchMyBooks();

</script>